// src/pages/Wallet/modals/TokenInfoModal.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { WalletContext } from '../../../contexts/WalletContext';
import { AiOutlineCopy, AiOutlineCheck, AiOutlineSend } from 'react-icons/ai';
import { FiExternalLink, FiShare2 } from 'react-icons/fi';
import { BsArrowUpRight, BsArrowDownRight } from 'react-icons/bs';
import { FaTelegram, FaTwitter, FaGlobe } from 'react-icons/fa';
import { BiCategoryAlt } from 'react-icons/bi';
import { ethers, Contract, formatUnits } from 'ethers';
import { generateUniversalDeepLink, isTelegramWebApp } from '../../../utils/deepLinkUtils';
import secureFetch from '../../../utils/secureFetch'; 
import TokenChart from './TokenChart';
import NativeTokenChart from './NativeTokenChart';
import './TokenInfoModal.css';
import './TokenChart.css';

// Minimal ERC-20 ABI with just the functions we need
const ERC20_ABI = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)"
];

const TokenInfoModal = ({ 
  isOpen, 
  onClose, 
  token, 
  tokenBalance, 
  nativePrice,
  tokenPrice, 
  onSend
}) => {
  // Add these states and refs for share functionality
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [copiedWebLink, setCopiedWebLink] = useState(false);
  const [copiedTelegramLink, setCopiedTelegramLink] = useState(false);
  const shareOptionsRef = useRef(null);
  
  // Original state variables
  const { t } = useTranslation();
  const { currentChain } = useContext(NetworkContext);
  const { provider } = useContext(WalletContext);
  const navigate = useNavigate();
  
  const [copied, setCopied] = useState(false);
  const [tokenSupply, setTokenSupply] = useState(null);
  const [formattedSupply, setFormattedSupply] = useState(null);
  const [isLoadingSupply, setIsLoadingSupply] = useState(false);
  
  // Add GeckoTerminal token info state
  const [tokenInfo, setTokenInfo] = useState(null);
  const [isLoadingTokenInfo, setIsLoadingTokenInfo] = useState(false);
  
  // Get native token symbol from current chain
  const nativeTokenSymbol = currentChain && currentChain.nativeTokenName
    ? currentChain.nativeTokenName.includes('(')
      ? currentChain.nativeTokenName.split('(')[1].replace(')', '').trim()
      : currentChain.nativeTokenName
    : 'ETH';
  
  // Is this the native token? - UPDATED CHECK to also check for type === "native"
  const isNative = token.type === "native" || !token.address || token.address.toLowerCase() === 'native';
  
  // Format token balance
  const formattedBalance = parseFloat(tokenBalance).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6
  });
  
  // Calculate USD value if price is available
  const usdValue = tokenPrice && tokenBalance 
    ? (parseFloat(tokenBalance) * tokenPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;
  
  // Calculate FDV if supply and price are available
  const fullyDilutedValue = tokenSupply && tokenPrice
    ? (tokenSupply * tokenPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;
  
  // Share functionality
  // Generate share links
  const getShareLinks = () => {
    if (!currentChain?.key) return { webUrl: '', telegramUrl: '' };
    
    const tokenAddr = !isNative ? token.address : null;
    return generateUniversalDeepLink(currentChain.key, tokenAddr);
  };
  
  const { webUrl, telegramUrl } = getShareLinks();
  const inTelegram = isTelegramWebApp();
  
  // Toggle share options dropdown
  const handleToggleShareOptions = () => {
    setShowShareOptions(!showShareOptions);
  };
  
  // Copy web link to clipboard
  const handleCopyWebLink = () => {
    navigator.clipboard.writeText(webUrl);
    setCopiedWebLink(true);
    setTimeout(() => setCopiedWebLink(false), 2000);
  };
  
  // Copy Telegram link to clipboard
  const handleCopyTelegramLink = () => {
    navigator.clipboard.writeText(telegramUrl);
    setCopiedTelegramLink(true);
    setTimeout(() => setCopiedTelegramLink(false), 2000);
  };
  
  // Open in Telegram
  const handleOpenInTelegram = () => {
    window.open(telegramUrl, '_blank');
  };
  
  // Close share options dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareOptionsRef.current && !shareOptionsRef.current.contains(event.target)) {
        setShowShareOptions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Copy address to clipboard
  const handleCopy = () => {
    if (!token.address || isNative) return;
    
    navigator.clipboard.writeText(token.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Open block explorer
  const openExplorer = () => {
    if (!token.address || isNative || !currentChain?.explorerUrl) return;
    
    const url = `${currentChain.explorerUrl}/token/${token.address}`;
    window.open(url, '_blank');
  };
  
  // Handle Send button click
  const handleSend = () => {
    onClose();
    if (onSend) {
      onSend(token);
    }
  };
  
  // Handle Buy button click (redirect to swap page)
  const handleBuy = () => {
    if (!token || !token.symbol) return;
    
    // Close modal first
    onClose();
    
    // Navigate to swap page with detailed token information
    navigate('/swap', { 
      state: { 
        action: 'buy', 
        token: {
          address: token.address || 'native',
          symbol: token.symbol || nativeTokenSymbol,
          name: token.name || token.symbol || nativeTokenSymbol,
          decimals: token.decimals || 18
        }
      }
    });
    
    console.log("Navigating to buy:", token.symbol);
  };
  
  // Handle Sell button click (redirect to swap page)
  const handleSell = () => {
    if (!token || !token.symbol) return;
    
    // Close modal first
    onClose();
    
    // Navigate to swap page with detailed token information
    navigate('/swap', { 
      state: { 
        action: 'sell', 
        token: {
          address: token.address || 'native',
          symbol: token.symbol || nativeTokenSymbol,
          name: token.name || token.symbol || nativeTokenSymbol,
          decimals: token.decimals || 18
        }
      }
    });
    
    console.log("Navigating to sell:", token.symbol);
  };
  
  // Open website URL
  const openWebsite = (url) => {
    if (!url) return;
    window.open(url, '_blank');
  };
  
  // Open social media links
  const openTelegram = (handle) => {
    if (!handle) return;
    window.open(`https://t.me/${handle}`, '_blank');
  };
  
  const openTwitter = (handle) => {
    if (!handle) return;
    window.open(`https://twitter.com/${handle}`, '_blank');
  };
  
// Fetch token info from GeckoTerminal
const fetchTokenInfo = async () => {
  if (isNative || !token.address || token.address === 'native' || !currentChain?.geckoTerminalId) {
    return;
  }
  
  setIsLoadingTokenInfo(true);
  setTokenInfo(null);
  
  try {
    const networkId = currentChain.geckoTerminalId;
    const tokenAddress = token.address;
    const url = `https://api.geckoterminal.com/api/v2/networks/${networkId}/tokens/${tokenAddress}/info`;
    
    // Use secureFetch instead of fetch
    const response = await secureFetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch token info');
    }
    
    const data = await response.json();
    
    if (data && data.data && data.data.attributes) {
      setTokenInfo(data.data.attributes);
      console.log('GeckoTerminal token info:', data.data.attributes);
    }
  } catch (error) {
    console.error('Error fetching token info from GeckoTerminal:', error);
  } finally {
    setIsLoadingTokenInfo(false);
  }
};
  
  // Fetch token supply directly from the token contract
  useEffect(() => {
    const fetchTokenSupply = async () => {
      if (isNative || !token.address || token.address === 'native' || !provider) {
        setIsLoadingSupply(false);
        return;
      }
      
      setIsLoadingSupply(true);
      setTokenSupply(null);
      setFormattedSupply(null);
      
      try {
        // Create contract instance
        const tokenContract = new Contract(token.address, ERC20_ABI, provider);
        
        // Get token decimals
        let decimals;
        try {
          // Try to get decimals from contract first
          decimals = await tokenContract.decimals();
        } catch (error) {
          console.log("Could not fetch decimals from contract, using default:", error);
          // Fall back to the decimals we already have or default to 18
          decimals = token.decimals || 18;
        }
        
        // Get total supply
        const totalSupply = await tokenContract.totalSupply();
        
        // Format with appropriate decimals
        const formattedTotalSupply = formatUnits(totalSupply, decimals);
        
        // Store both raw and formatted values
        setTokenSupply(parseFloat(formattedTotalSupply));
        setFormattedSupply(parseFloat(formattedTotalSupply).toLocaleString());
        
        console.log(`Fetched supply for ${token.symbol}:`, formattedTotalSupply);
      } catch (error) {
        console.error("Error fetching token supply from contract:", error);
      } finally {
        setIsLoadingSupply(false);
      }
    };
    
    if (isOpen && token.address && !isNative) {
      fetchTokenSupply();
    }
  }, [isOpen, token.address, token.decimals, token.symbol, isNative, provider]);
  
  // Fetch token info from GeckoTerminal
  useEffect(() => {
    if (isOpen && token.address && !isNative && currentChain?.geckoTerminalId) {
      fetchTokenInfo();
    }
  }, [isOpen, token.address, isNative, currentChain]);
  
  if (!isOpen) return null;
  
  return (
    <div className="token-info-modal-overlay">
      <div className="token-info-modal">
        <div className="token-info-header">
          <h2>
            {token.name || (isNative ? currentChain?.nativeTokenName : token.symbol)} 
            <span className="token-symbol">({token.symbol || nativeTokenSymbol})</span>
          </h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="token-info-content">
          <div className="token-balance-section">
            <div className="token-balance-title">{t('wallet.yourBalance')}</div>
            <div className="token-balance-value">{formattedBalance}</div>
            {usdValue && (
              <div className="token-usd-value">${usdValue}</div>
            )}
          </div>
          
          {/* Chart section - TokenChart for ERC-20 tokens, NativeTokenChart for native tokens */}
          {isNative ? (
            currentChain?.coingeckoId && (
              <NativeTokenChart 
                coinId={currentChain.coingeckoId} 
              />
            )
          ) : token.address && (
            <TokenChart 
              tokenAddress={token.address} 
              network={currentChain?.geckoTerminalId || currentChain?.key || 'bsc'} 
            />
          )}
          
          <div className="token-actions">
            <button className="action-button send-button-info" onClick={handleSend}>
              <AiOutlineSend />
              <span>{t('actions.send')}</span>
            </button>
            
            {!isNative && (
              <>
                <button className="action-button buy-button" onClick={handleBuy}>
                  <BsArrowDownRight />
                  <span>{t('wallet.buy')}</span>
                </button>
                
                <button className="action-button sell-button" onClick={handleSell}>
                  <BsArrowUpRight />
                  <span>{t('wallet.sell')}</span>
                </button>
              </>
            )}
            
            {/* Share button with dropdown */}
            <div className="action-button-wrapper">
              <button className="action-button share-button" onClick={handleToggleShareOptions}>
                <FiShare2 />
                <span>{t('tokenInfo.share', 'Share')}</span>
              </button>
              
              {showShareOptions && (
                <div className="share-options" ref={shareOptionsRef}>
                  <button onClick={handleCopyWebLink}>
                    {copiedWebLink ? (
                      <span className="copy-success">{t('tokenInfo.copied', 'Copied! ✓')}</span>
                    ) : (
                      <>
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        {t('tokenInfo.copyWebLink', 'Copy Web Link')}
                      </>
                    )}
                  </button>
                  
                  <button onClick={handleCopyTelegramLink}>
                    {copiedTelegramLink ? (
                      <span className="copy-success">{t('tokenInfo.copied', 'Copied! ✓')}</span>
                    ) : (
                      <>
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        {t('tokenInfo.copyTelegramLink', 'Copy Telegram Link')}
                      </>
                    )}
                  </button>
                  
                  {!inTelegram && (
                    <button onClick={handleOpenInTelegram} className="telegram-button">
                      <svg 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path d="M22 2L11 13"></path>
                        <path d="M22 2l-7 20-4-9-9-4 20-7z"></path>
                      </svg>
                      {t('tokenInfo.openInTelegram', 'Open in Telegram')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="token-info-section">
            <div className="token-info-row">
              <div className="info-label">{t('tokenInfo.token', 'Token')}</div>
              <div className="info-value">{token.symbol || nativeTokenSymbol}</div>
            </div>
            
            {!isNative && (
              <>
                <div className="token-info-row">
                  <div className="info-label">{t('wallet.decimals')}</div>
                  <div className="info-value">{token.decimals || 18}</div>
                </div>
                
                <div className="token-info-row">
                  <div className="info-label">{t('wallet.contract')}</div>
                  <div className="info-value contract-address">
                    <span className="address-text">
                      {token.address ? 
                        `${token.address.substring(0, 6)}...${token.address.substring(token.address.length - 4)}` 
                        : 'N/A'}
                    </span>
                    
                    {token.address && (
                      <>
                        {copied ? (
                          <AiOutlineCheck className="icon copy-icon success" />
                        ) : (
                          <AiOutlineCopy className="icon copy-icon" onClick={handleCopy} />
                        )}
                        
                        <FiExternalLink 
                          className="icon external-link" 
                          onClick={openExplorer} 
                        />
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
            
            {tokenPrice && (
              <div className="token-info-row">
                <div className="info-label">{t('wallet.price')}</div>
                <div className="info-value">${tokenPrice.toFixed(6)}</div>
              </div>
            )}
            
            {/* Only show supply sections for non-native tokens */}
            {!isNative && (
              <>
                {isLoadingSupply && (
                  <div className="token-info-row">
                    <div className="info-label">{t('wallet.supply')}</div>
                    <div className="info-value loading">{t('wallet.loading')}</div>
                  </div>
                )}
                
                {!isLoadingSupply && formattedSupply && (
                  <>
                    <div className="token-info-row">
                      <div className="info-label">{t('wallet.supply')}</div>
                      <div className="info-value">{formattedSupply}</div>
                    </div>
                    
                    {fullyDilutedValue && (
                      <div className="token-info-row">
                        <div className="info-label">{t('wallet.fdv')}</div>
                        <div className="info-value">${fullyDilutedValue}</div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          
          {/* GeckoTerminal Token Info Section */}
          {!isNative && tokenInfo && (
            <div className="token-info-section">
              <h3 className="token-info-subtitle">{t('tokenInfo.tokenInformation', 'Token Information')}</h3>
              
              {/* Social Links */}
              <div className="token-social-links">
                {tokenInfo.telegram_handle && (
                  <button 
                    className="social-button telegram" 
                    onClick={() => openTelegram(tokenInfo.telegram_handle)}
                  >
                    <FaTelegram />
                    <span>{t('tokenInfo.telegram', 'Telegram')}</span>
                  </button>
                )}
                
                {tokenInfo.twitter_handle && (
                  <button 
                    className="social-button twitter" 
                    onClick={() => openTwitter(tokenInfo.twitter_handle)}
                  >
                    <FaTwitter />
                    <span>{t('tokenInfo.twitter', 'Twitter')}</span>
                  </button>
                )}
                
                {tokenInfo.websites && tokenInfo.websites.length > 0 && (
                  <button 
                    className="social-button website" 
                    onClick={() => openWebsite(tokenInfo.websites[0])}
                  >
                    <FaGlobe />
                    <span>{t('tokenInfo.website', 'Website')}</span>
                  </button>
                )}
              </div>
              
              {/* Categories */}
              {tokenInfo.categories && tokenInfo.categories.length > 0 && (
                <div className="token-info-row">
                  <div className="info-label">
                    <BiCategoryAlt /> {t('tokenInfo.categories', 'Categories')}
                  </div>
                  <div className="info-value category-tags">
                    {tokenInfo.categories.map((category, index) => (
                      <span key={index} className="category-tag">
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Holders Info */}
              {tokenInfo.holders && tokenInfo.holders.count > 0 && (
                <div className="token-info-row">
                  <div className="info-label">{t('tokenInfo.holders', 'Holders')}</div>
                  <div className="info-value">
                    {tokenInfo.holders.count.toLocaleString()}
                    {tokenInfo.holders.distribution_percentage && (
                      <span className="holder-distribution">
                        {typeof tokenInfo.holders.distribution_percentage === 'object' ? (
                          <span title="Top 10 holders percentage">
                            ({t('tokenInfo.topTenHolders', 'Top 10')}: {parseFloat(tokenInfo.holders.distribution_percentage.top_10).toFixed(2)}%)
                          </span>
                        ) : (
                          `(${parseFloat(tokenInfo.holders.distribution_percentage).toFixed(2)}% ${t('tokenInfo.distributed', 'distributed')})`
                        )}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Description */}
              {tokenInfo.description && (
                <div className="token-description">
                  <h4>{t('tokenInfo.about', 'About')} {token.symbol || token.name}</h4>
                  <p>{tokenInfo.description}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenInfoModal;